import React from "react";
import { ShieldOff, CameraOff, Banknote } from "lucide-react";

const ITEMS = [
  {
    id: "deposit-fraud",
    num: "01",
    icon: ShieldOff,
    title: "Deposit Fraud",
    body:
      "Renters wire thousands as deposits with zero transparency on if — or when — that money comes back.",
    metric: "AED 5,000+",
    metricLabel: "typical at risk",
  },
  {
    id: "no-inspection",
    num: "02",
    icon: CameraOff,
    title: "No Inspection Record",
    body:
      "Pickup disputes turn into he-said-she-said. No timestamped proof of the car's condition before the keys changed hands.",
    metric: "9 of 10",
    metricLabel: "disputes unresolved",
  },
  {
    id: "hidden-fines",
    num: "03",
    icon: Banknote,
    title: "Hidden Fines",
    body:
      "Surprise RTA fines, Salik tolls and Dubai Police charges arrive weeks later — long after the trip is over.",
    metric: "AED 600",
    metricLabel: "avg surprise bill",
  },
];

export default function ProblemSection() {
  return (
    <section
      id="problem"
      data-testid="problem-section"
      className="relative py-24 sm:py-32 lg:py-40 bg-white border-t border-carvny-border"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        {/* Editorial header */}
        <div className="grid grid-cols-12 gap-y-8 lg:gap-x-12">
          <div className="col-span-12 lg:col-span-4">
            <div className="text-[11px] font-bold uppercase tracking-[0.28em] text-carvny-red">
              §01 — The Problem
            </div>
            <div className="rule mt-4" />
          </div>
          <div className="col-span-12 lg:col-span-8">
            <h2
              data-testid="problem-headline"
              className="font-display text-4xl sm:text-5xl lg:text-[64px] font-black tracking-tightest text-carvny-ink leading-[1.02]"
            >
              Peer-to-peer car rentals
              <br className="hidden sm:block" />
              <span className="font-serif-italic font-normal text-carvny-ink/85"> are broken </span>
              <span className="text-carvny-red">in three places.</span>
            </h2>
            <p className="mt-6 text-base sm:text-lg text-carvny-muted leading-relaxed max-w-2xl">
              Renting from a stranger today means trusting their word on the
              three most expensive parts of the transaction. We watched it go
              wrong one too many times.
            </p>
          </div>
        </div>

        {/* Cards */}
        <div className="mt-16 sm:mt-20 grid grid-cols-1 md:grid-cols-3 gap-px bg-carvny-border border border-carvny-border rounded-[28px] overflow-hidden">
          {ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <article
                key={item.id}
                data-testid={`problem-card-${item.id}`}
                className="group relative bg-white p-8 sm:p-10 transition-colors hover:bg-carvny-surface"
              >
                <div className="flex items-center justify-between">
                  <div className="w-11 h-11 rounded-xl bg-carvny-red-50 text-carvny-red flex items-center justify-center group-hover:bg-carvny-red group-hover:text-white transition-colors">
                    <Icon size={20} strokeWidth={2} />
                  </div>
                  <span className="font-display text-2xl font-black text-carvny-ink/15 tracking-tight">
                    {item.num}
                  </span>
                </div>

                <h3 className="font-display mt-10 text-[26px] sm:text-3xl font-bold tracking-tight text-carvny-ink leading-tight">
                  {item.title}
                </h3>
                <p className="mt-3 text-[15px] leading-relaxed text-carvny-muted max-w-sm">
                  {item.body}
                </p>

                <div className="mt-10 pt-6 border-t border-carvny-border flex items-baseline gap-3">
                  <span className="font-display text-2xl font-black text-carvny-red tracking-tight">
                    {item.metric}
                  </span>
                  <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-carvny-muted">
                    {item.metricLabel}
                  </span>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
